import { useState } from "react";
import { MapPin } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/lamka";
import { useAuth } from "@/context/AuthContext";

const FIELDS = [
  { key: "full_name", label: "Full name", required: true },
  { key: "phone", label: "Phone number", required: true, type: "tel" },
  { key: "area", label: "House / street / area", required: true },
  { key: "city", label: "Town / city", required: true },
  { key: "landmark", label: "Landmark (optional)" },
];

export const isAddressComplete = (a) => !!a && FIELDS.every((f) => !f.required || (a[f.key] || "").trim());
export const addressLine = (a) => [a.full_name, a.phone, a.area, a.landmark, a.city].filter(Boolean).join(", ");

export const AddressStep = ({ onConfirm }) => {
  const { user, setUser } = useAuth();
  const saved = user?.address || {};
  const [editing, setEditing] = useState(!isAddressComplete(saved));
  const [form, setForm] = useState({ full_name: saved.full_name || user?.name || "", phone: saved.phone || user?.phone || "", area: saved.area || "", city: saved.city || "", landmark: saved.landmark || "" });
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    if (!isAddressComplete(form)) return toast.error("Please fill in all required fields.");
    if (!/^\+?\d[\d\s-]{8,}$/.test(form.phone.trim())) return toast.error("Please enter a valid phone number.");
    setBusy(true);
    try {
      const address = Object.fromEntries(Object.entries(form).map(([k, v]) => [k, v.trim()]));
      await api.post("/auth/profile", { address });
      setUser((u) => ({ ...u, address }));
      toast.success("Address saved");
      onConfirm(address);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save your address. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!editing)
    return (
      <div data-testid="address-step" className="space-y-4">
        <p className="eyebrow">Delivery address</p>
        <div data-testid="saved-address" className="flex gap-4 p-5 border border-[#0F0F0F] bg-white">
          <MapPin className="h-5 w-5 text-[#C5A059] shrink-0 mt-0.5" strokeWidth={1.5} />
          <p className="text-sm leading-relaxed break-words min-w-0">{addressLine(saved)}</p>
        </div>
        <div className="flex gap-3">
          <button data-testid="address-confirm-button" onClick={() => onConfirm(saved)} className="flex-1 bg-[#0F0F0F] text-[#C5A059] py-4 text-xs tracking-[0.3em] uppercase hover:shadow-[0_0_30px_rgba(197,160,89,0.35)] transition-shadow">
            Deliver here
          </button>
          <button data-testid="address-edit-button" onClick={() => setEditing(true)} className="px-6 border border-[#0F0F0F]/30 text-xs tracking-[0.25em] uppercase hover:border-[#0F0F0F]">
            Edit
          </button>
        </div>
      </div>
    );

  return (
    <form data-testid="address-step" onSubmit={save} className="space-y-5">
      <p className="eyebrow">Delivery address</p>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-5">
        {FIELDS.map((f) => (
          <label key={f.key} className={f.key === "area" ? "sm:col-span-2" : ""}>
            <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-[#71717A] mb-1">{f.label}</span>
            <input
              data-testid={`address-input-${f.key}`}
              type={f.type || "text"}
              value={form[f.key]}
              onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
              className="w-full bg-white border border-[#0F0F0F]/15 focus:border-[#C5A059] outline-none px-3 py-2.5 text-sm"
            />
          </label>
        ))}
      </div>
      <button data-testid="address-save-button" type="submit" disabled={busy} className="w-full bg-[#0F0F0F] text-[#C5A059] py-4 text-xs tracking-[0.3em] uppercase disabled:opacity-40 hover:shadow-[0_0_30px_rgba(197,160,89,0.35)] transition-shadow">
        {busy ? "Saving…" : "Save & deliver here"}
      </button>
    </form>
  );
};
